import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Actions pour le reducer du panier
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

// Fonction utilitaire pour recalculer les totaux de manière robuste
const recalculateTotals = (items) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  return { totalItems, totalPrice };
};

// Reducer pour gérer l'état du panier
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM: {
      const { item, quantity } = action.payload;
      const existingItemIndex = state.items.findIndex(cartItem => cartItem.id === item.id);
      
      let updatedItems;
      if (existingItemIndex >= 0) {
        // Si l'article existe déjà, mettre à jour la quantité
        updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += quantity;
      } else {
        // Ajouter un nouvel article
        const newItem = {
          ...item,
          quantity,
          addedAt: new Date().toISOString()
        };
        updatedItems = [...state.items, newItem];
      }
      
      const { totalItems, totalPrice } = recalculateTotals(updatedItems);
      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice
      };
    }
    
    case CART_ACTIONS.REMOVE_ITEM: {
      const updatedItems = state.items.filter(item => item.id !== action.payload.itemId);
      const { totalItems, totalPrice } = recalculateTotals(updatedItems);
      
      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice
      };
    }
    
    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { itemId, quantity } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === itemId);
      
      if (itemIndex === -1) return state;
      
      if (quantity <= 0) {
        // Si la quantité est 0 ou négative, supprimer l'article
        const updatedItems = state.items.filter(item => item.id !== itemId);
        const { totalItems, totalPrice } = recalculateTotals(updatedItems);
        
        return {
          ...state,
          items: updatedItems,
          totalItems,
          totalPrice
        };
      }
      
      const updatedItems = [...state.items];
      updatedItems[itemIndex].quantity = quantity;
      const { totalItems, totalPrice } = recalculateTotals(updatedItems);
      
      return {
        ...state,
        items: updatedItems,
        totalItems,
        totalPrice
      };
    }
    
    case CART_ACTIONS.CLEAR_CART:
      return {
        items: [],
        totalItems: 0,
        totalPrice: 0
      };
    
    case CART_ACTIONS.LOAD_CART: {
      const loadedState = action.payload;
      // Recalculer les totaux pour s'assurer de leur exactitude
      const { totalItems, totalPrice } = recalculateTotals(loadedState.items || []);
      return {
        ...loadedState,
        totalItems,
        totalPrice
      };
    }
    
    default:
      return state;
  }
};

// État initial du panier
const initialCartState = {
  items: [],
  totalItems: 0,
  totalPrice: 0
};

// Créer le contexte
const CartContext = createContext();

// Hook personnalisé pour utiliser le contexte du panier
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Provider du contexte du panier
export const CartProvider = ({ children }) => {
  const [cartState, dispatch] = useReducer(cartReducer, initialCartState);

  // Fonction de validation et correction des totaux
  const validateAndCorrectTotals = (state) => {
    const { totalItems: calculatedItems, totalPrice: calculatedPrice } = recalculateTotals(state.items);
    
    // Vérifier si les totaux sont corrects
    if (state.totalItems !== calculatedItems || Math.abs(state.totalPrice - calculatedPrice) > 0.01) {
      console.warn('Correction des totaux du panier détectée:', {
        ancien: { totalItems: state.totalItems, totalPrice: state.totalPrice },
        nouveau: { totalItems: calculatedItems, totalPrice: calculatedPrice }
      });
      
      return {
        ...state,
        totalItems: calculatedItems,
        totalPrice: calculatedPrice
      };
    }
    
    return state;
  };

  // Charger le panier depuis localStorage au démarrage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsedCart });
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
      }
    }
  }, []);

  // Valider et corriger les totaux à chaque changement d'état
  const [validatedCartState, setValidatedCartState] = React.useState(cartState);
  
  useEffect(() => {
    const correctedState = validateAndCorrectTotals(cartState);
    setValidatedCartState(correctedState);
  }, [cartState]);

  // Sauvegarder le panier dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(validatedCartState));
  }, [validatedCartState]);

  // Fonctions d'action
  const addToCart = (item, quantity = 1) => {
    dispatch({
      type: CART_ACTIONS.ADD_ITEM,
      payload: { item, quantity }
    });
  };

  const removeFromCart = (itemId) => {
    dispatch({
      type: CART_ACTIONS.REMOVE_ITEM,
      payload: { itemId }
    });
  };

  const updateQuantity = (itemId, quantity) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { itemId, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  // Fonction utilitaire pour obtenir la quantité d'un article spécifique
  const getItemQuantity = (itemId) => {
    const item = validatedCartState.items.find(item => item.id === itemId);
    return item ? item.quantity : 0;
  };

  // Fonction utilitaire pour vérifier si un article est dans le panier
  const isInCart = (itemId) => {
    return validatedCartState.items.some(item => item.id === itemId);
  };

  const value = {
    ...validatedCartState,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isInCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;